import { useState, useRef, useCallback } from 'react';
import { Team, Group, Venue, GroupMatch } from '../types/tournament';
import { usePredictions } from '../context/PredictionContext';
import { getTeamById, getVenueById, formatMatchDate } from '../hooks/useBracketData';

interface GroupStageProps {
  groups: Group[];
  teams: Team[];
  venues: Venue[];
  groupMatches: GroupMatch[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function GroupStage({
  groups,
  teams,
  venues,
  groupMatches,
  isCollapsed,
  onToggleCollapse,
}: GroupStageProps) {
  return (
    <div
      className={`bg-gray-50 border-r border-gray-200 h-full transition-all duration-300 flex flex-col
        ${isCollapsed ? 'w-12' : 'w-80'}
      `}
    >
      {/* Header */}
      <div className="bg-fifa-blue text-white p-3 flex items-center justify-between">
        {!isCollapsed && <h2 className="font-bold">Group Stage</h2>}
        <button
          onClick={onToggleCollapse}
          className="p-1 hover:bg-white/20 rounded transition-colors"
        >
          {isCollapsed ? '→' : '←'}
        </button>
      </div>

      {/* Groups list */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto p-2 space-y-3">
          {groups.map(group => (
            <GroupCard
              key={group.id}
              group={group}
              teams={teams}
              venues={venues}
              matches={groupMatches.filter(m => m.group === group.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface GroupCardProps {
  group: Group;
  teams: Team[];
  venues: Venue[];
  matches: GroupMatch[];
}

function GroupCard({ group, teams, venues, matches }: GroupCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getGroupStandings, setGroupPrediction } = usePredictions();
  const currentStandings = getGroupStandings(group.id);

  const groupTeams = group.teams.map(id => getTeamById(teams, id)).filter(Boolean) as Team[];

  // Get ordered teams based on predictions or default order
  const orderedTeams = currentStandings.length > 0
    ? currentStandings.map(id => getTeamById(teams, id)).filter(Boolean) as Team[]
    : groupTeams;

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartIndex = useRef<number | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTouchDragging, setIsTouchDragging] = useState(false);

  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const newOrder = [...orderedTeams];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setGroupPrediction(group.id, newOrder.map(t => t.id));
  }, [orderedTeams, group.id, setGroupPrediction]);

  // Desktop drag handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    if (fromIndex !== null) {
      handleReorder(fromIndex, toIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartIndex.current = index;

    // Long press to start dragging
    longPressTimer.current = setTimeout(() => {
      setIsTouchDragging(true);
      setDraggedIndex(index);
      setDragOverIndex(index);
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 400);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Cancel long press if moved before timer fires
    if (!isTouchDragging) {
      if (longPressTimer.current && touchStartY.current !== null) {
        const moveDistance = Math.abs(e.touches[0].clientY - touchStartY.current);
        if (moveDistance > 10) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      }
      return;
    }

    // Prevent scrolling while dragging
    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const teamRow = elements.find(el => el.getAttribute('data-team-index') !== null);

    if (teamRow) {
      const overIndex = parseInt(teamRow.getAttribute('data-team-index') || '-1');
      if (overIndex >= 0) {
        setDragOverIndex(overIndex);
      }
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    if (isTouchDragging && draggedIndex !== null && dragOverIndex !== null) {
      handleReorder(draggedIndex, dragOverIndex);
    }

    setIsTouchDragging(false);
    setDraggedIndex(null);
    setDragOverIndex(null);
    touchStartY.current = null;
    touchStartIndex.current = null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Group header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full bg-gray-100 px-3 py-2 flex items-center justify-between hover:bg-gray-200 transition-colors"
      >
        <span className="font-bold text-fifa-blue">{group.name}</span>
        <span className="text-gray-500 text-sm">{isExpanded ? '▼' : '▶'}</span>
      </button>

      {/* Team standings (always visible) - drag and drop enabled */}
      <div
        className="divide-y divide-gray-100"
        style={{ touchAction: isTouchDragging ? 'none' : 'auto' }}
      >
        {orderedTeams.map((team, index) => (
          <div
            key={team.id}
            data-team-index={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(e, index)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className={`px-3 py-2 flex items-center gap-2 text-sm cursor-grab active:cursor-grabbing select-none transition-all
              ${index < 2 ? 'bg-green-50' : index === 2 ? 'bg-yellow-50' : 'bg-red-50'}
              ${draggedIndex === index ? 'opacity-50 scale-95' : ''}
              ${dragOverIndex === index && draggedIndex !== index ? 'border-t-2 border-fifa-blue' : ''}
              ${isTouchDragging && draggedIndex === index ? 'shadow-lg z-10 scale-105' : ''}
            `}
            style={{ touchAction: isTouchDragging ? 'none' : 'auto' }}
          >
            <span className="w-5 text-center font-bold text-gray-500">{index + 1}</span>
            <span className="text-lg">{team.flag || '🏳️'}</span>
            <span className="flex-1 font-medium">{team.code}</span>
            <span className="text-gray-400 text-xs">⋮⋮</span>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="px-3 py-1 bg-gray-50 text-xs text-gray-500 flex gap-3">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-400 rounded-full"></span> R32
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-yellow-400 rounded-full"></span> Possible R32
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-red-400 rounded-full"></span> Out
        </span>
      </div>

      {/* Matches (expandable) */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-2 space-y-2 bg-gray-50">
          <div className="text-xs font-medium text-gray-500 px-1">Matches</div>
          {matches.map(match => {
            const team1 = getTeamById(teams, match.team1Id);
            const team2 = getTeamById(teams, match.team2Id);
            const venue = getVenueById(venues, match.venueId);

            return (
              <div key={match.id} className="bg-white rounded p-2 text-xs">
                <div className="flex justify-between text-gray-500 mb-1">
                  <span>M{match.matchNumber}</span>
                  <span>{formatMatchDate(match.date)}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span>{team1?.flag}</span>
                  <span className="font-medium">{team1?.code}</span>
                  <span className="text-gray-400">vs</span>
                  <span className="font-medium">{team2?.code}</span>
                  <span>{team2?.flag}</span>
                </div>
                <div className="text-gray-500 text-center mt-1">{venue?.city}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
